import React from 'react';

/**
 * Shown while a lazily-loaded page chunk is still downloading.
 *
 * Every page in App.js is a separate code-split chunk, so without this the
 * browser shows a blank white screen between navigation and the page appearing.
 *
 * It deliberately fades in after a short delay: chunks that are already cached
 * resolve in a few milliseconds, and flashing a spinner for those looks worse
 * than showing nothing. Slow loads still get feedback well within the moment a
 * visitor starts to wonder whether the click registered.
 *
 * Markup matches the boot loader in public/index.html so the handoff from the
 * static HTML shell to React is seamless.
 */
const PageLoader = ({ label = 'Loading' }) => (
  <div className="page-loader" role="status" aria-live="polite">
    <div className="page-loader-inner">
      <img
        className="page-loader-logo"
        src={process.env.PUBLIC_URL + '/assets/img/logo.png'}
        alt=""
        aria-hidden="true"
      />
      <div className="page-loader-ring" aria-hidden="true"></div>
      <p className="page-loader-text">{label}</p>
    </div>

    <style>{`
      .page-loader {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
        z-index: 9998;
        opacity: 0;
        animation: page-loader-in 0.25s ease 0.15s forwards;
      }
      .page-loader-inner {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
      }
      .page-loader-logo {
        width: 130px;
        max-width: 45vw;
        height: auto;
      }
      .page-loader-ring {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        border: 3px solid rgba(0, 0, 0, 0.09);
        border-top-color: var(--thm-base, #b01933);
        animation: page-loader-spin 0.8s linear infinite;
      }
      .page-loader-text {
        margin: 0;
        font-size: 14px;
        letter-spacing: 0.02em;
        color: #6b7280;
      }
      @keyframes page-loader-in {
        to { opacity: 1; }
      }
      @keyframes page-loader-spin {
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .page-loader { animation-delay: 0.15s; animation-duration: 0.01s; }
        .page-loader-ring { animation-duration: 2s; }
      }
    `}</style>
  </div>
);

export default PageLoader;
